import React from 'react';
import { useTranslation } from 'react-i18next';
import { tv } from '@/utils/tv';
import { CateringChoice } from '@/components/AbsenceApplication/types';
import { Alert } from '@/components/Icons/Alert';
import { Radio } from '@/components/Radio';

const cateringRow = tv({
  base: 'flex h-[54px] w-full cursor-pointer items-center justify-between rounded-xl py-2 pr-3 pl-4 text-left transition-colors',
  variants: {
    isSelected: {
      true: 'bg-info-surface ring-[1.5px] ring-primary ring-inset',
      false: 'bg-on-primary-surface',
    },
  },
});

const cateringLabel = tv({
  base: 'text-subhead-14',
  variants: {
    isSelected: {
      true: 'text-primary',
      false: 'text-muted-foreground',
    },
  },
});

interface Props {
  value: CateringChoice | null;
  showNote?: boolean;
  onChange: (value: CateringChoice) => void;
}

const options = [
  {
    value: CateringChoice.Needed,
    labelKey: 'absenceApplication.cateringNeeded',
  },
  {
    value: CateringChoice.NotNeeded,
    labelKey: 'absenceApplication.cateringNotNeeded',
  },
] as const;

export const AbsenceCateringChoice: React.FC<Props> = ({
  value,
  showNote,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <p className="text-subhead-14 text-foreground">
          {t('absenceApplication.cateringQuestion')}
        </p>

        {showNote && (
          <div className="flex items-start gap-2 px-0.5">
            <Alert className="size-4 shrink-0 text-destructive" />
            <p className="text-body-13 text-muted-foreground md:text-body-14">
              {t('absenceApplication.cateringNote')}
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={cateringRow({ isSelected })}
            >
              <span className={cateringLabel({ isSelected })}>
                {t(option.labelKey)}
              </span>
              <Radio checked={isSelected} />
            </button>
          );
        })}
      </div>
    </div>
  );
};
